import { type Job, type Amendment } from '@/features/jobs/Job'
import { type TableSortOrder } from '@/components/Table'
import { action, computed, observable } from 'mobx'
import { orderBy } from 'lodash-es'

interface AmendmentTableColumn {
  key: keyof Pick<
    Amendment,
    | 'description'
    | 'visibility'
    | 'customerAmount'
    | 'providerAmount'
    | 'dateCreated'
  >
  header: string
  sortable?: boolean
}

const amendmentTableColumns: AmendmentTableColumn[] = [
  {
    key: 'description',
    header: 'Description',
    sortable: true,
  },
  {
    key: 'visibility',
    header: 'Visibility',
    sortable: true,
  },
  {
    key: 'customerAmount',
    header: 'Client price',
    sortable: true,
  },
  {
    key: 'providerAmount',
    header: 'Pro cut',
    sortable: true,
  },
  {
    key: 'dateCreated',
    header: 'Date',
    sortable: true,
  },
]

/**
 * View model for the sortable table of a job's amendments.
 */
export class AmendmentsTableViewModel {
  /**
   * The table's columns.
   */
  readonly columns = amendmentTableColumns

  /**
   * The default sort order when first listing the amendments in the table.
   */
  @observable accessor sortOrder: TableSortOrder = 'desc'

  /**
   * The default sort field (column) when first listing the amendments in the table.
   */
  @observable accessor sortField: AmendmentTableColumn['key'] = 'dateCreated'

  constructor(private readonly _job: Job) {}

  /**
   * Ordered amendments to display.
   */
  @computed
  get orderedAmendments() {
    return orderBy(
      this._job.amendments,
      [this.sortField],
      this.sortOrder === 'none' ? undefined : this.sortOrder,
    )
  }

  /**
   * Set the sorting order in the specified field (column).
   */
  @action.bound
  setSort(sortField: AmendmentTableColumn['key'], sortOrder: TableSortOrder) {
    this.sortField = sortField
    this.sortOrder = sortOrder
  }
}
